import type { AvailabilityResponse, HotelOverviewData } from '../types';

export interface FetchAvailabilityParams {
  apiUrl: string;
  hotelSlug: string;
  roomId: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  locale?: string;
}

/**
 * Fetches availability data from the Momentje widget API
 */
export async function fetchAvailability(params: FetchAvailabilityParams): Promise<AvailabilityResponse> {
  const { apiUrl, hotelSlug, roomId, startDate, endDate, locale = 'en' } = params;

  const url = new URL(`${apiUrl}/api/widget/availability`);
  url.searchParams.set('hotelSlug', hotelSlug);
  url.searchParams.set('roomId', roomId);
  url.searchParams.set('startDate', startDate);
  url.searchParams.set('endDate', endDate);
  url.searchParams.set('locale', locale);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to fetch availability: ${response.statusText}`
    );
  }

  const data = await response.json();

  // Convert date strings to Date objects
  if (data.data) {
    data.data = data.data.map((item: any) => ({
      ...item,
      created_at: new Date(item.created_at),
      updated_at: new Date(item.updated_at),
    }));
  }

  return data;
}

export interface FetchHotelOverviewParams {
  apiUrl: string;
  hotelSlug: string;
  locale?: string;
}

/**
 * Fetches hotel overview data (all rooms) from the Momentje widget API
 */
export async function fetchHotelOverview(params: FetchHotelOverviewParams): Promise<HotelOverviewData> {
  const { apiUrl, hotelSlug, locale = 'en' } = params;

  const url = new URL(`${apiUrl}/api/widget/hotel-overview`);
  url.searchParams.set('hotelSlug', hotelSlug);
  url.searchParams.set('locale', locale);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to fetch hotel overview: ${response.statusText}`
    );
  }

  const data = await response.json();
  return data;
}
